/**
 * Broadcast / News-channel variant — screen-local design system.
 *
 * Aesthetic: a 24/7 financial-news / live-broadcast control-room display.
 * Channel chrome — a state-coloured breaking-news bar, a persistent "LIVE" bug,
 * an anchor lower-third for the top recommendation, a small studio backdrop for
 * the plant, and a single scrolling alert ribbon along the bottom. Loud and
 * authoritative, but trustworthy: motion only ever means "this just changed".
 *
 * Like the terminal's `TERM` palette, every value here is composed FROM design
 * tokens (no invented hexes) so the screen stays a self-contained dark studio,
 * independent of the app's light/dark theme.
 */
import { dark, semantic, typography } from '@/shared/theme/tokens';

export const BCAST = {
  studio:     dark.bgPage,          // #0f1219 — the studio void behind everything
  panel:      dark.bg,              // #141927 — card / lower-third surface
  chrome:     dark.bgSubtle,        // #1a2030 — straps, kickers, channel chrome
  inset:      dark.bgMuted,         // #1e2536 — deepest inset cell
  line:       dark.border,          // hairline divider
  lineBright: dark.borderInput,     // emphasised hairline
  ink:        dark.textPrimary,     // #e2e8f0 — anchor copy
  inkDim:     dark.textMuted,       // #b0bec5 — supporting labels
  inkFaint:   dark.textSubtle,      // #a0aec0 — least important
  brand:      semantic.brandAccent, // #3EB5E7 — the channel identity cyan
  live:       semantic.errorAlt,    // #ef4444 — the broadcast "LIVE" red
  crit:       semantic.errorAlt,    // #ef4444
  warn:       semantic.warningSolid,// #f59e0b
  ok:         semantic.successAlt,  // #10b981
  mono:       typography.fontFamily.mono,
  heading:    typography.fontFamily.heading,
};

// Presentation-layer keyframes. Each is gated per-element on the model's
// `animate` flag (false under reduced-motion). The ONLY ambient motion is the
// scrolling alert ribbon and the live-bug pulse — both signal "this is live".
export const KEYFRAMES = `
@keyframes bc-pulse  { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
@keyframes bc-bug    { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(0.84); } }
@keyframes bc-marquee{ from { transform: translateX(0); } to { transform: translateX(-50%); } }
@keyframes bc-fade   { from { opacity: 0; } to { opacity: 1; } }
`;

// How many recs sit in the "UP NEXT" rail before collapsing into "+N more".
export const UP_NEXT_VISIBLE = 4;

// Marquee duration scales with the number of ticker segments so a long alert
// list doesn't whip past; clamped so a short list still drifts at a TV pace.
export function tickerDurationMs(segmentCount) {
  return Math.min(80_000, Math.max(26_000, segmentCount * 5200));
}
